import { type FC, useCallback } from 'react'

import { FanResponseDto } from '@api'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import TextareaAutosize from 'react-textarea-autosize'

import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import {
  FORM_ID,
  type FormValues,
  schema,
} from '@/dialogs/NoticeDialog/validation.ts'
import type { DialogProps } from '@/dialogs/types'
import { useUpdateNotice } from '@/modules/chatting/hooks/use-update-notice.ts'
import { ListUser } from '@/modules/chatting/types.ts'
import { CreatorAvatar } from '@/modules/creators/ui/CreatorAvatar.tsx'

interface Props extends DialogProps {
  user: FanResponseDto | ListUser
  notice: string | null
  onEdit?: (notice: string) => void
}

export const NoticeDialog: FC<Props> = ({
  isOpen,
  onClose,
  user,
  notice,
  onEdit,
}) => {
  const { id, name, avatar, avatarThumbs, displayName, username } = user
  const onSuccess = useCallback(
    (notice: string) => {
      onEdit?.(notice)
      onClose()
    },
    [onClose, onEdit],
  )

  const updateNoticeMutation = useUpdateNotice(id, onSuccess)

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      notice: notice ?? undefined,
    },
  })

  const onSubmit = (values: FormValues) => {
    return updateNoticeMutation.mutateAsync(values.notice)
  }

  return (
    <Dialog
      show={isOpen}
      onClose={onClose}
      showCloseIcon
    >
      <Dialog.Header>
        <Dialog.Title>Edit Notes</Dialog.Title>
      </Dialog.Header>
      <Dialog.Description className="mt-6 block space-y-6">
        <div className="flex items-center gap-3">
          <CreatorAvatar
            size={40}
            avatarSrc={avatarThumbs?.c50 ?? avatarThumbs?.c144 ?? avatar}
            name={displayName ?? name}
            className="shrink-0"
          />
          <div className="flex flex-col">
            <span className="font-medium leading-6 text-title">{name}</span>
            <span className="text-xs leading-5 text-secondary-300">
              @{username}
            </span>
          </div>
        </div>
        <div className="border-b border-border-secondary" />
        <div className="flex flex-col">
          <label className="mb-[6px] text-sm font-medium text-title">
            Note
          </label>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            id={FORM_ID}
          >
            <TextareaAutosize
              {...form.register('notice')}
              minRows={6}
              maxRows={12}
              className="min-h-[180px] w-full resize-none rounded-md border border-border-secondary px-3 py-4 text-title focus:border-primary-500 focus:outline-0 dark:bg-secondary-600"
              placeholder="Enter Notes"
            />
          </form>
        </div>
      </Dialog.Description>
      <Dialog.ClosableFooter
        onClose={onClose}
        className="grid-cols-3"
      >
        <Button
          onClick={() => form.setValue('notice', '')}
          variant="outline"
          disabled={form.formState.isSubmitting}
        >
          Clear
        </Button>
        <Button
          type="submit"
          form={FORM_ID}
          isLoading={form.formState.isSubmitting}
        >
          Save
        </Button>
      </Dialog.ClosableFooter>
    </Dialog>
  )
}
